import React, { useEffect, useMemo, useState } from "react"
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Link,
  Menu,
  MenuItem,
  CircularProgress,
  useMediaQuery,
  TextField,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import GetAppIcon from "@material-ui/icons/GetApp"
import RefreshIcon from "@material-ui/icons/Refresh"
import ClearAllIcon from "@material-ui/icons/ClearAll"
import moment from "moment"
import useStore from "src/v2/utils/store"
import { useAlertStore } from "src/v2/utils/alert-store"
import CustomSearch from "../form-components/custom-search"
import CustomInputSelect from "../form-components/custom-input-select"
import CustomButton from "../form-components/custom-button"
import CustomModal from "../ui-components/custom-modal"

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  filtersPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filtersHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  dateField: {
    width: "100%",
  },
  tableContainer: {
    maxHeight: 600,
  },
  tableHeadCell: {
    fontWeight: 700,
    backgroundColor: "#fafafa",
    whiteSpace: "nowrap",
  },
  resultChip: {
    fontWeight: 600,
    textTransform: "capitalize",
    minWidth: 80,
  },
  resultSuccess: {
    backgroundColor: "#e6f4ea",
    color: "#137333",
    border: "1px solid #a8dab5",
  },
  resultFailure: {
    backgroundColor: "#fce8e6",
    color: "#c5221f",
    border: "1px solid #f4b4b0",
  },
  resultDenied: {
    backgroundColor: "#fff4e5",
    color: "#8a5a00",
    border: "1px solid #ffcc80",
  },
  resultUnknown: {
    backgroundColor: "#eceff1",
    color: "#455a64",
    border: "1px solid #cfd8dc",
  },
  viewLink: {
    cursor: "pointer",
    fontWeight: 500,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  detailRow: {
    display: "flex",
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  detailKey: {
    fontWeight: 600,
    minWidth: 160,
    color: theme.palette.text.secondary,
  },
  detailValue: {
    flex: 1,
    wordBreak: "break-word",
    fontFamily: "monospace",
    fontSize: 13,
  },
  detailPre: {
    margin: 0,
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    fontSize: 12,
    maxHeight: 240,
    overflow: "auto",
    width: "100%",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  summary: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
}))

const RESULT_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "failure", label: "Failure" },
  { value: "denied", label: "Denied" },
]

const DEFAULT_START_DAYS_BACK = 30

function toDateInput(m) {
  return m.format("YYYY-MM-DD")
}

function resultChipClass(classes, result) {
  const r = (result || "").toLowerCase()
  if (r === "success") return classes.resultSuccess
  if (r === "failure") return classes.resultFailure
  if (r === "denied") return classes.resultDenied
  return classes.resultUnknown
}

// Fields we consider "primary" and show in a labeled block. Everything else
// is dumped underneath as raw JSON so admins never lose visibility.
const DETAIL_FIELDS = [
  ["_id", "Log ID"],
  ["created_iso", "Created (ISO)"],
  ["created", "Created (epoch ms)"],
  ["event_type", "Event Type"],
  ["operation", "Operation"],
  ["result", "Result"],
  ["http_status", "HTTP Status"],
  ["user_email", "User Email"],
  ["user_name", "User Name"],
  ["account_id", "Account ID"],
  ["ip_address", "IP Address"],
  ["user_agent", "User Agent"],
  ["review_id", "Review ID"],
  ["review_name", "Review Name"],
  ["simulation_id", "Simulation ID"],
  ["simulation_name", "Simulation Name"],
  ["engagement_id", "Engagement ID"],
  ["engagement_name", "Engagement Name"],
  ["api_key", "API Key"],
  ["error_code", "Error Code"],
  ["error_message", "Error Message"],
]

function renderPrimitive(value) {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

function csvEscape(value) {
  if (value === null || value === undefined) return ""
  let s = typeof value === "object" ? JSON.stringify(value) : String(value)
  if (s.includes('"') || s.includes(",") || s.includes("\n")) {
    s = `"${s.replace(/"/g, '""')}"`
  }
  return s
}

const CSV_COLUMNS = [
  "created_iso",
  "event_type",
  "operation",
  "result",
  "http_status",
  "user_email",
  "user_name",
  "account_id",
  "ip_address",
  "review_id",
  "review_name",
  "simulation_id",
  "simulation_name",
  "engagement_id",
  "engagement_name",
  "error_code",
  "error_message",
]

function download(filename, content, mime) {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function AuditLog() {
  const classes = useStyles()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"))

  const auditLogs = useStore((s) => s.auditLogs)
  const auditLogsLoading = useStore((s) => s.auditLogsLoading)
  const fetchAuditLogs = useStore((s) => s.fetchAuditLogs)
  const showAlert = useAlertStore((s) => s.showAlert)

  const [startDate, setStartDate] = useState(
    toDateInput(moment().subtract(DEFAULT_START_DAYS_BACK, "days")),
  )
  const [endDate, setEndDate] = useState(toDateInput(moment()))

  // Individual search fields per user request — one input per column.
  const [filters, setFilters] = useState({
    eventType: "",
    operation: "",
    userEmail: "",
    userName: "",
    ipAddress: "",
    reviewName: "",
    simulationName: "",
    result: "",
  })

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const [detailLog, setDetailLog] = useState(null)
  const [downloadAnchor, setDownloadAnchor] = useState(null)

  useEffect(() => {
    fetchAuditLogs(startDate, endDate, showAlert)
    // eslint-disable-next-line
  }, [])

  const handleReload = () => {
    fetchAuditLogs(startDate, endDate, showAlert)
    setPage(0)
  }

  const handleClearAll = () => {
    setFilters({
      eventType: "",
      operation: "",
      userEmail: "",
      userName: "",
      ipAddress: "",
      reviewName: "",
      simulationName: "",
      result: "",
    })
    setPage(0)
  }

  const updateFilter = (key) => (value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(0)
  }

  const filteredLogs = useMemo(() => {
    const contains = (haystack, needle) => {
      if (!needle) return true
      if (haystack === null || haystack === undefined) return false
      return String(haystack).toLowerCase().includes(needle.toLowerCase())
    }
    return (auditLogs || []).filter((log) => {
      if (!contains(log.event_type, filters.eventType)) return false
      if (!contains(log.operation, filters.operation)) return false
      if (!contains(log.user_email, filters.userEmail)) return false
      if (!contains(log.user_name, filters.userName)) return false
      if (!contains(log.ip_address, filters.ipAddress)) return false
      if (!contains(log.review_name, filters.reviewName)) return false
      if (!contains(log.simulation_name, filters.simulationName)) return false
      if (filters.result && (log.result || "").toLowerCase() !== filters.result)
        return false
      return true
    })
  }, [auditLogs, filters])

  const pagedLogs = useMemo(() => {
    const startIdx = page * rowsPerPage
    return filteredLogs.slice(startIdx, startIdx + rowsPerPage)
  }, [filteredLogs, page, rowsPerPage])

  const handleDownloadCsv = () => {
    setDownloadAnchor(null)
    if (!filteredLogs.length) {
      showAlert &&
        showAlert({
          message: "No logs to download.",
          severity: "warning",
          title: "Nothing to export",
        })
      return
    }
    const header = CSV_COLUMNS.join(",")
    const rows = filteredLogs.map((log) =>
      CSV_COLUMNS.map((col) => csvEscape(log[col])).join(","),
    )
    const csv = [header, ...rows].join("\n")
    download(
      `audit-logs-${startDate}-to-${endDate}.csv`,
      csv,
      "text/csv;charset=utf-8;",
    )
  }

  const handleDownloadJson = () => {
    setDownloadAnchor(null)
    if (!filteredLogs.length) {
      showAlert &&
        showAlert({
          message: "No logs to download.",
          severity: "warning",
          title: "Nothing to export",
        })
      return
    }
    download(
      `audit-logs-${startDate}-to-${endDate}.json`,
      JSON.stringify(filteredLogs, null, 2),
      "application/json",
    )
  }

  const formatCreated = (log) => {
    if (log.created_iso) return moment(log.created_iso).format("YYYY-MM-DD HH:mm:ss")
    if (log.created) return moment(log.created).format("YYYY-MM-DD HH:mm:ss")
    return "—"
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.filtersPaper} elevation={1}>
        <Box className={classes.filtersHeader}>
          <Typography variant="h6" className={classes.filtersTitle}>
            Audit Log
          </Typography>
          <Box className={classes.filtersActions}>
            <CustomButton
              variant="link"
              onClick={handleClearAll}
              endIcon={<ClearAllIcon />}
            >
              Clear all
            </CustomButton>
            <CustomButton
              variant="link"
              onClick={handleReload}
              endIcon={<RefreshIcon />}
              loading={auditLogsLoading}
            >
              Reload
            </CustomButton>
            <CustomButton
              onClick={(e) => setDownloadAnchor(e.currentTarget)}
              endIcon={<GetAppIcon />}
            >
              Download
            </CustomButton>
            <Menu
              anchorEl={downloadAnchor}
              open={Boolean(downloadAnchor)}
              onClose={() => setDownloadAnchor(null)}
            >
              <MenuItem onClick={handleDownloadCsv}>Download as CSV</MenuItem>
              <MenuItem onClick={handleDownloadJson}>Download as JSON</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="From date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.dateField}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="To date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.dateField}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <CustomButton onClick={handleReload} loading={auditLogsLoading}>
              Apply date range
            </CustomButton>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <CustomSearch
              value={filters.eventType}
              onChange={updateFilter("eventType")}
              placeholder="Event type..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CustomSearch
              value={filters.operation}
              onChange={updateFilter("operation")}
              placeholder="Operation..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CustomSearch
              value={filters.userEmail}
              onChange={updateFilter("userEmail")}
              placeholder="User email..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CustomSearch
              value={filters.userName}
              onChange={updateFilter("userName")}
              placeholder="User name..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CustomSearch
              value={filters.ipAddress}
              onChange={updateFilter("ipAddress")}
              placeholder="IP address..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CustomSearch
              value={filters.reviewName}
              onChange={updateFilter("reviewName")}
              placeholder="Review name..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CustomSearch
              value={filters.simulationName}
              onChange={updateFilter("simulationName")}
              placeholder="Simulation name..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CustomInputSelect
              label="Result"
              value={filters.result}
              onChange={updateFilter("result")}
              options={RESULT_OPTIONS}
            />
          </Grid>
        </Grid>
      </Paper>

      <Typography className={classes.summary}>
        Showing {filteredLogs.length} of {(auditLogs || []).length} logs
        {" — "}
        {startDate} to {endDate}
      </Typography>

      <Paper elevation={1}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size={isSmall ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>Time</TableCell>
                <TableCell className={classes.tableHeadCell}>Event</TableCell>
                <TableCell className={classes.tableHeadCell}>Operation</TableCell>
                <TableCell className={classes.tableHeadCell}>User</TableCell>
                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                <TableCell className={classes.tableHeadCell}>Review</TableCell>
                <TableCell className={classes.tableHeadCell}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLogsLoading && pagedLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className={classes.emptyState}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!auditLogsLoading && filteredLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className={classes.emptyState}>
                    No audit logs match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {pagedLogs.map((log) => (
                <TableRow key={log._id || `${log.created}-${log.event_type}`} hover>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    {formatCreated(log)}
                  </TableCell>
                  <TableCell>{log.event_type || "—"}</TableCell>
                  <TableCell>{log.operation || "—"}</TableCell>
                  <TableCell>{log.user_email || log.user_name || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={log.result || "unknown"}
                      className={`${classes.resultChip} ${resultChipClass(
                        classes,
                        log.result,
                      )}`}
                    />
                  </TableCell>
                  <TableCell style={{ maxWidth: 220 }}>
                    {log.review_name || "—"}
                  </TableCell>
                  <TableCell>
                    <Link
                      className={classes.viewLink}
                      onClick={() => setDetailLog(log)}
                    >
                      View details
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredLogs.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <CustomModal
        showModal={Boolean(detailLog)}
        size="large"
        title="Audit Log Details"
        onCloseModal={() => setDetailLog(null)}
        cancelBtnText="Close"
      >
        {detailLog && (
          <Box>
            {DETAIL_FIELDS.map(([key, label]) => (
              <Box key={key} className={classes.detailRow}>
                <Typography className={classes.detailKey}>{label}</Typography>
                <Typography className={classes.detailValue} component="div">
                  {key === "result" ? (
                    <Chip
                      size="small"
                      label={detailLog[key] || "unknown"}
                      className={`${classes.resultChip} ${resultChipClass(
                        classes,
                        detailLog[key],
                      )}`}
                    />
                  ) : (
                    renderPrimitive(detailLog[key])
                  )}
                </Typography>
              </Box>
            ))}
            {detailLog.metadata && (
              <Box className={classes.detailRow}>
                <Typography className={classes.detailKey}>Metadata</Typography>
                <pre className={classes.detailPre}>
                  {JSON.stringify(detailLog.metadata, null, 2)}
                </pre>
              </Box>
            )}
            <Box className={classes.detailRow}>
              <Typography className={classes.detailKey}>Raw JSON</Typography>
              <pre className={classes.detailPre}>
                {JSON.stringify(detailLog, null, 2)}
              </pre>
            </Box>
          </Box>
        )}
      </CustomModal>
    </Box>
  )
}
